import {frameworkVersionSchema, routineSourceRefSchema} from './framework-version.types';
import {z} from "zod";
import {permissionObservationsSchema} from "./resource-permissions.types";
import {routineIdentitySchema, routinePlatformSchema, routineResourceKindSchema, routineResourceRequirementSchema} from "./routine-definition.types";
import {firmwareManifestSchema, glassesSoftwareRefSchema} from "./glasses-software.types";
import {candidateVerificationSchema} from './candidate-verification.types';
import type {RoutineLiveProgress} from './routine-progress.types';

export const frameworkIdentitySchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,239}$/);
export const frameworkBuildSchema = z.object({
  repository: z.string().regex(/^[\w-]+\/[\w.-]+$/),
  headSha: z.string().regex(/^[a-f0-9]{40}$/),
  channel: z.enum(["dev", "staging", "pr", "local"]),
  prNumber: z.number().int().positive().optional(),
  manifest: firmwareManifestSchema.optional(),
  manifestSha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).passthrough().superRefine((build, ctx) => {
  if (build.channel === "pr" && !build.prNumber) ctx.addIssue({code: "custom", message: "PR build requires its PR number"});
});
const frozenFrameworkRequestInputSchema = z.object({
  routineId: routineIdentitySchema,
  routineSource: routineSourceRefSchema,
  minimumFrameworkVersion: frameworkVersionSchema.optional(),
  definitionRevision: z.string().regex(/^[a-f0-9]{40}$/),
  platform: routinePlatformSchema,
  laneId: frameworkIdentitySchema,
  build: frameworkBuildSchema,
  resources: z.array(z.object({
    id: frameworkIdentitySchema,
    kind: routineResourceKindSchema,
    laneId: frameworkIdentitySchema.optional(),
    capabilities: z.array(routineIdentitySchema).max(30).optional(),
    permissionObservations: permissionObservationsSchema.optional(),
  }).strict().refine(resource => resource.capabilities === undefined || routineResourceRequirementSchema.safeParse({
    kind: resource.kind, capabilities: resource.capabilities,
  }).success, "Resource capabilities must match their declared kind")),
  policy: z.record(z.unknown()).optional(),
  glassesModel: z.literal('nimo').optional(),
  glassesStart: glassesSoftwareRefSchema.optional(),
  glassesReturn: glassesSoftwareRefSchema.optional(),
  verification: candidateVerificationSchema.optional(),
}).passthrough();
const recordedInputSchema = frozenFrameworkRequestInputSchema.extend({routineSource: routineSourceRefSchema.optional()});
function validateFrameworkRequestInput(input: z.infer<typeof recordedInputSchema>, ctx: z.RefinementCtx) {
  if (input.verification && input.verification.sourceRevision !== input.definitionRevision)
    ctx.addIssue({code: 'custom', message: 'Candidate verification source differs from the request'});
  if (input.routineSource && input.routineSource.commit !== input.definitionRevision)
    ctx.addIssue({code: 'custom', message: 'Routine source differs from its definition revision'});
  const glasses = input.resources.some(resource => resource.kind === "glasses");
  if (input.glassesModel && (!glasses || input.platform !== 'android' || input.glassesStart || input.glassesReturn))
    ctx.addIssue({code: 'custom', message: 'NIMO requires its selected Android glasses resource without Mentra Live firmware references'});
  if (input.glassesModel && (!input.routineSource || input.routineSource.minimumRoutineApiVersion < 43))
    ctx.addIssue({code: 'custom', message: 'NIMO connection requires routine API 43 or later'});
  if (input.glassesModel && (input.build.manifest !== undefined || input.build.manifestSha256 !== undefined))
    ctx.addIssue({code: 'custom', message: 'NIMO executable build cannot contain Mentra Live firmware references'});
  if ((glasses && !input.glassesModel && !(input.glassesStart && input.glassesReturn)) || (!glasses && (input.glassesStart || input.glassesReturn)) ||
    (input.glassesStart === undefined) !== (input.glassesReturn === undefined))
    ctx.addIssue({code: "custom", message: "Selected glasses resource requires frozen start and return software references"});
  if (input.glassesStart && input.glassesReturn) {
    if (input.glassesStart.model !== input.glassesReturn.model)
      ctx.addIssue({code: "custom", message: "Starting and return software must name the same selected glasses model"});
    if (!input.build.manifest || input.build.manifestSha256 !== input.build.manifest.sha256 ||
      JSON.stringify(input.glassesReturn.manifest) !== JSON.stringify(input.build.manifest))
      ctx.addIssue({code: "custom", message: "Return glasses software must match the selected build manifest identity"});
  }
}
/** New admission always requires the exact published routine bundle. */
export const frameworkRequestInputSchema = frozenFrameworkRequestInputSchema.superRefine(validateFrameworkRequestInput);
/** Historical display only: preserve requests recorded before bundle provenance, without a fallback source. */
export const recordedFrameworkRequestInputSchema = recordedInputSchema.superRefine(validateFrameworkRequestInput);

/** Keep the frozen app selection; NIMO has no Live glasses firmware component. */
export function frameworkExecutableBuild(build: z.infer<typeof frameworkBuildSchema>, glassesModel?: 'nimo') {
  if (glassesModel !== 'nimo') return build;
  const {manifest: _manifest, manifestSha256: _manifestSha256, ...app} = build;
  return app;
}

/** Admin delivery projection; it describes a request without claiming execution evidence. */
export interface FrameworkRequestDisplay {
  requestId: string; hostId?: string; inputSha256?: string; dispatchIntentSha256?: string; routineId: string; platform: string;
  definitionRevision: string; routineSource?: z.infer<typeof routineSourceRefSchema>; minimumFrameworkVersion?: number;
  laneId?: string; build: z.infer<typeof frameworkBuildSchema>;
  state: "awaiting-source" | "awaiting-runner" | "preparing" | "queued" | "accepted" | "running" | "terminal"; terminalStatus?: string;
  createdAt?: string; acceptedAt?: string; reason?: string; reasonAt?: string;
  cancellationRequested?: boolean; cancellationAcknowledged?: boolean;
  assignment?: {jobId: string; hostId: string; laneId: string; actionsRunId: string; actionsJobId: string};
  actionsRuns?: Array<{actionsRunId: string; recordedAt: string}>;
  preparationDisposition?: 'not-run' | 'not-applicable';
  live?: RoutineLiveProgress;
  liveUnavailable?: boolean;
  waiting?: import('./routine-progress.types').RoutineQueuedBlock;
}
